import { jwtDecode } from "jwt-decode";
import { authenticateSSO, getAddInOrigin, testGraphAPIAccess } from "common";

const TOKEN_EXPIRY_BUFFER_SEC = 300;
const IDENTITY_UNSUPPORTED_CODE = 13000;
const SSO_SERVER_ERROR_CODE = 13007;

let identityApiUnsupported = false;
let identityApiUnsupportedLogged = false;
let ssoServerErrorLogged = false;

export const isLocalhostAddIn = () => getAddInOrigin() === "local";

export const extractGraphTokenFromSsoResponse = (response) =>
  response?.result?.access_token ?? response?.access_token ?? null;

export const isIdentityApiUnsupported = () => identityApiUnsupported;

export const isGraphTokenExpired = (token) => {
  if (!token) {
    return true;
  }
  try {
    const { exp } = jwtDecode(token);
    if (!exp) {
      return false;
    }
    return Date.now() / 1000 >= exp - TOKEN_EXPIRY_BUFFER_SEC;
  } catch {
    return true;
  }
};

const logIdentityApiUnsupported = () => {
  if (identityApiUnsupportedLogged) {
    return;
  }
  identityApiUnsupportedLogged = true;
  console.warn(
    "[graphToken] Office Identity API is not available for this add-in (error 13000). " +
      "Graph body fallback cannot use SSO. Common causes:\n" +
      "  - Sideloaded manifest without <WebApplicationInfo> (e.g. manifest.local.xml before fix)\n" +
      "  - Installed manifest URL does not match the add-in origin (localhost vs outlook-dev.unicli.ai)\n" +
      "  - Add-in needs to be removed and re-sideloaded after manifest changes\n" +
      "Fix: use manifest.Dev.xml for outlook-dev, or manifest.local.xml with WebApplicationInfo for localhost, then re-sideload."
  );
};

const logSsoServerError = (error) => {
  if (ssoServerErrorLogged) {
    return;
  }
  ssoServerErrorLogged = true;
  const code = error?.code ?? error?.errorCode;
  const localhost = isLocalhostAddIn();
  console.warn(
    `[graphToken] Office SSO server error (${code || "unknown"}): ${error?.message || error}.` +
      (localhost
        ? "\n\nLocalhost sideload requires your local Entra app to expose an Application ID URI matching manifest.local.xml <Resource>, e.g.:\n" +
          "  api://localhost:3000/{your-local-app-client-id}\n" +
          "  (must match manifest.local.xml <Resource> exactly)\n" +
          "Also verify in Entra: Enterprise applications → your app → Properties →\n" +
          "  'Enabled for users to sign-in?' = Yes, and Office client apps are pre-authorized.\n" +
          "Dev (outlook-dev.unicli.ai) uses a different URI — both cannot work until Azure has the localhost URI added."
        : "\nVerify Entra app registration: Application ID URI matches manifest <Resource>,\n" +
          "sign-in enabled for users, and admin consent granted for Mail.Read.")
  );
};

const markIdentityUnsupported = (error) => {
  const code = error?.code ?? error?.errorCode;
  const message = error?.message || "";
  if (code === IDENTITY_UNSUPPORTED_CODE || message.includes("identity API is not supported")) {
    identityApiUnsupported = true;
    logIdentityApiUnsupported();
    return true;
  }
  return false;
};

const handleAccessTokenError = (error, apiName) => {
  if (markIdentityUnsupported(error)) {
    return true;
  }
  const code = error?.code ?? error?.errorCode;
  if (code === SSO_SERVER_ERROR_CODE) {
    logSsoServerError(error);
    return true;
  }
  console.warn(`[graphToken] ${apiName} failed:`, {
    code,
    message: error?.message || String(error),
  });
  return false;
};

export const isSsoOboAudienceMismatch = (response) => {
  const err = response?.result?.error ?? response?.error;
  const desc = response?.result?.error_description ?? response?.error_description ?? "";
  return err === "invalid_grant" && (desc.includes("AADSTS500131") || desc.includes("Assertion audience"));
};

export const resolveGraphTokenFromSso = async (response, officeToken) => {
  if (isSsoOboAudienceMismatch(response)) {
    console.warn(
      "[graphToken] Backend OBO failed (AADSTS500131): Xano auth/sso is configured for the dev middle-tier app " +
        "(unicli-outlook-addin-dev / 0ea99144-...) but the Office token is for your local add-in app (65b82862-...). " +
        "Fix: on backend app 0ea99144 add API permission for api://localhost:3000/65b82862-.../access_as_user, " +
        "and authorize 0ea99144 on local add-in app 65b82862. Or use forMSGraphAccess (see below)."
    );
  }

  let graphToken = extractGraphTokenFromSsoResponse(response);
  if (!graphToken && officeToken && (await testGraphAPIAccess(officeToken))) {
    graphToken = officeToken;
  }
  return graphToken;
};

/** Request a Microsoft Graph token directly from Office (bypasses backend OBO). */
export const getOfficeGraphAccessToken = async () => {
  if (identityApiUnsupported) {
    return null;
  }

  const makeGraphOptions = () => ({
    allowSignInPrompt: true,
    allowConsentPrompt: true,
    forMSGraphAccess: true,
  });

  if (typeof OfficeRuntime?.auth?.getAccessToken === "function") {
    try {
      const token = await OfficeRuntime.auth.getAccessToken(makeGraphOptions());
      if (token && (await testGraphAPIAccess(token))) {
        console.log("[graphToken] acquired Graph token via OfficeRuntime (forMSGraphAccess)");
        return token;
      }
    } catch (error) {
      handleAccessTokenError(error, "OfficeRuntime.auth.getAccessToken(forMSGraphAccess)");
    }
  }

  if (typeof Office?.auth?.getAccessToken === "function") {
    try {
      const token = await Office.auth.getAccessToken(makeGraphOptions());
      if (token && (await testGraphAPIAccess(token))) {
        console.log("[graphToken] acquired Graph token via Office.auth (forMSGraphAccess)");
        return token;
      }
    } catch (error) {
      handleAccessTokenError(error, "Office.auth.getAccessToken(forMSGraphAccess)");
    }
  }

  return null;
};

export const getOfficeAccessToken = async () => {
  if (identityApiUnsupported) {
    return null;
  }

  const makeOptions = () => ({
    allowSignInPrompt: true,
    allowConsentPrompt: true,
  });

  if (typeof OfficeRuntime?.auth?.getAccessToken === "function") {
    try {
      return await OfficeRuntime.auth.getAccessToken(makeOptions());
    } catch (error) {
      handleAccessTokenError(error, "OfficeRuntime.auth.getAccessToken");
    }
  }

  if (typeof Office?.auth?.getAccessToken === "function") {
    try {
      return await Office.auth.getAccessToken(makeOptions());
    } catch (error) {
      handleAccessTokenError(error, "Office.auth.getAccessToken");
    }
  }

  if (typeof Office?.auth?.getAccessTokenAsync === "function") {
    try {
      return await new Promise((resolve, reject) => {
        Office.auth.getAccessTokenAsync(makeOptions(), (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(result.error);
          }
        });
      });
    } catch (error) {
      handleAccessTokenError(error, "Office.auth.getAccessTokenAsync");
    }
  }

  return null;
};

export const acquireGraphAPIToken = async ({
  existingToken = null,
  authToken = null,
  user = null,
  login = null,
  forceRefresh = false,
} = {}) => {
  if (existingToken && !forceRefresh && !isGraphTokenExpired(existingToken)) {
    console.log("[graphToken] using existing token");
    return existingToken;
  }

  if (identityApiUnsupported) {
    return existingToken && !isGraphTokenExpired(existingToken) ? existingToken : null;
  }

  try {
    console.log("[graphToken] acquiring via Office SSO...");
    const graphDirect = await getOfficeGraphAccessToken();
    if (graphDirect) {
      if (login && authToken && user) {
        login(authToken, user, graphDirect);
      }
      return graphDirect;
    }

    const officeToken = await getOfficeAccessToken();
    if (!officeToken) {
      return existingToken && !isGraphTokenExpired(existingToken) ? existingToken : null;
    }

    const { preferred_username: email } = jwtDecode(officeToken);
    const ssoResult = await authenticateSSO(email, officeToken, getAddInOrigin());

    let graphToken = extractGraphTokenFromSsoResponse(ssoResult.response);
    let tokenSource = "sso-backend";

    if (!graphToken) {
      console.warn("[graphToken] SSO response had no access_token, trying Office token for Graph...");
      const works = await testGraphAPIAccess(officeToken);
      if (works) {
        graphToken = officeToken;
        tokenSource = "office-token-direct";
      }
    }

    if (!graphToken) {
      console.warn("[graphToken] could not acquire Graph token");
      return existingToken && !isGraphTokenExpired(existingToken) ? existingToken : null;
    }

    console.log("[graphToken] acquired via", tokenSource);

    const persistAuthToken = authToken ?? ssoResult.authToken;
    const persistUser = user ?? ssoResult.user;
    if (login && persistAuthToken && persistUser) {
      login(persistAuthToken, persistUser, graphToken);
    }

    return graphToken;
  } catch (error) {
    if (markIdentityUnsupported(error)) {
      return existingToken && !isGraphTokenExpired(existingToken) ? existingToken : null;
    }
    console.warn("[graphToken] acquire failed:", error?.message || error);
    return existingToken && !isGraphTokenExpired(existingToken) ? existingToken : null;
  }
};
